<template>
  <ResourceCombobox
    :model-value="multiple ? null : (modelValue ?? null)"
    :selected-values="multiple ? (modelValues ?? []) : []"
    :multiple="multiple"
    :items="options"
    :loading="loading"
    :loading-more="loadingMore"
    :has-more="hasMore"
    :disabled="disabled"
    :placeholder="placeholder || t('jobs.fields.jobType')"
    :button-class="buttonClass"
    :clearable="clearable"
    :compact="compact"
    :placeholder-only="placeholderOnly"
    :trigger-shape="triggerShape"
    :hide-options="creating"
    @update:model-value="v => emit('update:modelValue', v)"
    @update:selected-values="v => emit('update:modelValues', v)"
    @search="onSearch"
    @load-more="onLoadMore"
    @open-change="onOpenChange"
  >
    <template #icon><slot name="icon" /></template>
    <template #header><slot name="header" /></template>
    <!-- `flex`, never `inline-flex`: an inline-flex box is shrink-to-fit, so it
         sizes to its content and OVERFLOWS its parent instead of being bounded by
         it — the inner `truncate` then has no width to ellipsize against and the
         label just runs under the checkmark (user QA). Block-level flex fills the
         parent, which forces the label span to shrink and show its ellipsis. -->
    <template #selected="{ item }">
      <span class="flex min-w-0 items-center gap-1.5">
        <TypeMark :type-id="item.raw ? item.value : null" :size="22" />
        <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
      </span>
    </template>
    <template #item="{ item }">
      <span class="flex min-w-0 items-center gap-1.5">
        <TypeMark :type-id="item.raw ? item.value : null" :size="22" />
        <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
      </span>
    </template>

    <!-- CREATE-IN-PLACE: the caller answers the phone, and « come see my roof »
         only types the lead if « Toiture » is already in the catalog. Two fields,
         because that is everything with a near-term consequence: the name, and the
         evaluation SLOT (blank = the kind default, and a wrong slot mis-times the
         day the look-see lands on). The program is seeded at signature and the crew
         coverage at composition — both are later, and both are visible as debt on
         the Types de job page. A type is never auto-minted: a per-job type is not a
         catalog, and a fresh type in NOBODY's quote lane would fail CLOSED on a
         restricted crew — the dispatcher NAMES it, once. -->
    <template v-if="allowCreate" #footer="{ searchTerm, close }">
      <div v-if="creating" class="grid gap-2 border-t p-2" data-testid="type-create-step">
        <Input
          v-model="draftName"
          :placeholder="t('templates.fields.namePlaceholder')"
          data-testid="type-create-name"
          @keydown.enter.prevent="onCreate(close)"
        />
        <Input
          v-model="draftMinutes"
          type="number"
          min="1"
          :placeholder="t('templates.evaluation.durationPlaceholder')"
          data-testid="type-create-minutes"
          @keydown.enter.prevent="onCreate(close)"
        />
        <div class="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" @click="creating = false">
            {{ t('common.cancel') }}
          </Button>
          <Button
            type="button"
            size="sm"
            :loading="saving"
            :disabled="!draftName.trim()"
            data-testid="type-create-submit"
            @click="onCreate(close)"
          >
            {{ t('common.create') }}
          </Button>
        </div>
      </div>
      <button
        v-else-if="canCreate(searchTerm)"
        type="button"
        class="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-sunken"
        data-testid="type-create-row"
        @click="startCreate(searchTerm)"
      >
        <Plus class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span class="min-w-0 flex-1 truncate">{{ t('templates.createNamed', { name: searchTerm.trim() }) }}</span>
      </button>
    </template>
  </ResourceCombobox>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Plus } from 'lucide-vue-next'
import { useJobTemplates } from '@/composables/useJobTemplates'
import ResourceCombobox from '@/components/shared/ResourceCombobox.vue'
import TypeMark from '@/components/shared/TypeMark.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { Resource } from '@/api/resources'

// String-id v-model (single) or string[] (multiple) — the URL is the source of
// truth for filter state, so this combobox speaks ids, not resource objects, and
// self-hydrates names for ids it hasn't fetched yet (restored from the URL).
const props = withDefaults(defineProps<{
  modelValue?: string | null
  modelValues?: string[]
  multiple?: boolean
  includeNone?: boolean
  disabled?: boolean
  placeholder?: string
  buttonClass?: string
  clearable?: boolean
  compact?: boolean
  // Offers « + Créer le type "X" » when the search matches no existing name. On
  // for the surfaces where a type is being CHOSEN for real work (both intake
  // forms, the job page's tag) — off for filters, lenses, and capability lanes,
  // where minting a type mid-pick is nonsense.
  allowCreate?: boolean
  // The "select + list" pattern's ADD half: the trigger keeps its placeholder and
  // the caller renders the selection (JobTemplateList) as stacked rows.
  placeholderOnly?: boolean
  // Passed straight through: `pill` for a control standing in prose (the job
  // masthead's type tag), `input` everywhere a form field is what it is.
  triggerShape?: 'input' | 'pill' | 'add'
  // ⚠ No lane filter, on purpose. `evaluation_by_default` is a type's funnel
  // DEFAULT, never a gate: every door offers every type, and « Reprendre »
  // replays a pair on that promise (IntakeMenu validates by existence alone).
  // A picker that filtered by lane would hand the menu a prefill it cannot list.
  // ⭐ The already-resolved resource for `modelValue`, when the caller HAS it.
  // Without this the trigger can only learn its own label by fetching: the
  // hydration watch below awaits `fetchJobTemplates`, so a page that already
  // holds the template in its `included` still rendered « Sans type » for a beat
  // and then filled in — the detail arriving after the page had already loaded.
  // Seeding removes the flash AND the request (the full list is fetched on open,
  // never on mount, so a seeded combobox makes no call at all until touched).
  seed?: Resource<'job_template'> | Resource<'job_template'>[] | null
  // Types this pick must not offer — the ones already on the caller's surface
  // (the grouped dialog's rows, the offer's own type). Hidden from the list;
  // never a reason to refuse a value the caller already holds.
  excludeIds?: string[]
  // Choices that are not types, listed after them — the migration's
  // « Ne pas importer ». The caller owns what the value means.
  extraOptions?: { value: string; label: string }[]
}>(), {
  clearable: true,
  compact: false,
  allowCreate: false,
  seed: null,
  excludeIds: () => [],
  extraOptions: () => [],
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'update:modelValues': [value: string[]]
}>()

const { t } = useI18n()
const {
  jobTemplates, loading, loadingMore, hasMore, fetchJobTemplates, loadMoreJobTemplates,
  create: createJobTemplate,
} = useJobTemplates()

// Names for URL-restored ids survive a later list-replacing fetch (search/paging).
const hydrated = ref<Resource<'job_template'>[]>([])

// Caller-supplied resources count as known FIRST, so the hydration watch below
// finds nothing unknown and never fires.
const seeded = computed<Resource<'job_template'>[]>(() => {
  const s = props.seed
  if (!s) return []
  return Array.isArray(s) ? s : [s]
})

const known = computed(() => {
  const map = new Map<string, Resource<'job_template'>>()
  for (const tpl of seeded.value) map.set(tpl.id, tpl)
  for (const tpl of hydrated.value) map.set(tpl.id, tpl)
  for (const tpl of jobTemplates.value) map.set(tpl.id, tpl)
  return map
})

const options = computed(() => {
  const opts: { value: string; label: string; raw?: Resource<'job_template'> }[] = []
  if (props.includeNone) opts.push({ value: 'none', label: t('jobs.noType') })
  const excluded = new Set(props.excludeIds ?? [])
  for (const tpl of known.value.values()) {
    if (excluded.has(tpl.id) && !selectedIds.value.includes(tpl.id)) continue
    opts.push({ value: tpl.id, label: tpl.attributes.name, raw: tpl })
  }
  for (const extra of props.extraOptions ?? []) opts.push(extra)
  return opts
})

const selectedIds = computed(() =>
  props.multiple ? (props.modelValues ?? []) : (props.modelValue ? [props.modelValue] : [])
)

// Pin every selected id into `hydrated` so a later list-replacing fetch (search,
// paging) can't drop its chip; ids we don't have yet (e.g. restored from the URL
// before any fetch) are fetched first. `none` is a synthetic option.
watch(selectedIds, async (ids) => {
  const unknown = ids.filter(id => id !== 'none' && !known.value.has(id))
  if (unknown.length) {
    await fetchJobTemplates({ filter: { job_template_ids: unknown } })
  }
  for (const id of ids) {
    if (id === 'none') continue
    const tpl = known.value.get(id)
    if (tpl && !hydrated.value.some(h => h.id === tpl.id)) hydrated.value.push(tpl)
  }
}, { immediate: true })

// ── Create-in-place ─────────────────────────────────────────────────────────
const creating = ref(false)
const saving = ref(false)
const draftName = ref('')
const draftMinutes = ref('')

// Offer creation only for a term that names something NEW: an exact (trimmed,
// case-insensitive) hit on a listed type means the thing already exists, and a
// second « Toit » beside « Toit » is how a catalog turns into a job list.
const canCreate = (term: string): boolean => {
  const name = term.trim()
  if (!name) return false
  return ![...known.value.values()].some(
    tpl => tpl.attributes.name.trim().toLowerCase() === name.toLowerCase()
  )
}

const startCreate = (term: string) => {
  draftName.value = term.trim()
  draftMinutes.value = ''
  creating.value = true
}

const onCreate = async (close: () => void) => {
  const name = draftName.value.trim()
  if (!name || saving.value) return
  saving.value = true
  try {
    // Blank minutes = "nothing special about this type" ⇒ null, never 0: the slot
    // falls back to the kind default, and a zero-minute stop is a lie to the solver.
    // v-model on a number input hands back a number, not the string it was typed
    // as — normalize rather than assume either.
    const typed = String(draftMinutes.value ?? '').trim()
    const minutes = Number(typed)
    const created = await createJobTemplate({
      name,
      evaluation_duration_minutes: typed && minutes > 0 ? minutes : null,
    })
    if (!created) return // the composable already surfaced the error; stay in the step
    hydrated.value.push(created)
    if (props.multiple) emit('update:modelValues', [...(props.modelValues ?? []), created.id])
    else emit('update:modelValue', created.id)
    creating.value = false
    close()
  } finally {
    saving.value = false
  }
}

// Load the full picklist the first time the dropdown opens; searching re-queries.
const fullListLoaded = ref(false)
const onOpenChange = (open: boolean) => {
  if (open && !fullListLoaded.value) {
    fetchJobTemplates({ page: { number: 1, size: 50 } })
    fullListLoaded.value = true
  }
  // A dismissed popover is a dropped conversation: never reopen mid-create.
  if (!open) creating.value = false
}
const onSearch = (term: string) => {
  fetchJobTemplates({ q: term || undefined, page: { number: 1, size: 50 } })
  fullListLoaded.value = true
}
const onLoadMore = () => loadMoreJobTemplates()
</script>
